import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useSnackbar } from 'notistack';
import noImputableService, {
  NoImputable,
  MAX_NO_IMPUTABLE_DESCRIPCION_LEN,
  MAX_NO_IMPUTABLE_NOMBRE_LEN,
  trimNoImputableDescripcion,
  trimNoImputableNombreCuenta,
  trimNoImputableForFormFields
} from '../../services/noImputableService';

const defaultForm: NoImputable = {
  nroRubro: 0,
  descripcioncta: '',
  nombreCuenta: '',
  segmento: 0,
  totalizaEn: undefined,
  saldo: undefined
};

const LISTADO_NO_IMPUTABLES = '/contabilidad/noimputables';

const NoImputableForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroRubro: nroRubroParam } = useParams<{ nroRubro: string }>();
  const [searchParams] = useSearchParams();
  const totalizaEnFromQuery = searchParams.get('totalizaEn');
  const [formData, setFormData] = useState<NoImputable>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rubroOptions, setRubroOptions] = useState<NoImputable[]>([]);

  /** Igual que «Atrás» del navegador; si no hay historial, vuelve al listado. */
  const goBackOrListado = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(LISTADO_NO_IMPUTABLES);
    }
  };

  const loadNoImputable = useCallback(async () => {
    const nro = parseInt(nroRubroParam || '', 10);
    if (!nroRubroParam || nroRubroParam === 'nuevo' || isNaN(nro)) return;
    try {
      setLoading(true);
      const noImputable = await noImputableService.getNoImputable(nro);
      setFormData(trimNoImputableForFormFields(noImputable));
    } catch (error) {
      enqueueSnackbar('Error al cargar el no imputable', { variant: 'error' });
      navigate(LISTADO_NO_IMPUTABLES);
    } finally {
      setLoading(false);
    }
  }, [nroRubroParam, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroRubroParam && nroRubroParam !== 'nuevo') loadNoImputable();
  }, [nroRubroParam, loadNoImputable]);

  useEffect(() => {
    if (nroRubroParam !== 'nuevo') return;
    const base: NoImputable = { ...defaultForm };
    let te: number | undefined;
    if (totalizaEnFromQuery) {
      const n = parseInt(totalizaEnFromQuery, 10);
      if (!Number.isNaN(n) && n > 0) te = n;
    }
    setFormData(te != null ? { ...base, totalizaEn: te } : { ...base });
  }, [nroRubroParam, totalizaEnFromQuery]);

  useEffect(() => {
    noImputableService.getAllNoImputablesPlanCuentas()
      .then((list) => setRubroOptions(list.sort((a, b) => a.nroRubro - b.nroRubro)))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDescripcionBlur = () => {
    setFormData((prev) => ({ ...prev, descripcioncta: trimNoImputableDescripcion(prev.descripcioncta) }));
  };

  const handleNombreCuentaBlur = () => {
    setFormData((prev) => ({
      ...prev,
      nombreCuenta: trimNoImputableNombreCuenta(prev.nombreCuenta) ?? ''
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'nroRubro' || name === 'segmento' || name === 'totalizaEn') {
      const numVal = value === '' ? undefined : Number(value);
      setFormData(prev => ({ ...prev, [name]: numVal ?? 0 }));
    } else if (name === 'saldo') {
      const numVal = value === '' ? undefined : parseFloat(value);
      setFormData(prev => ({ ...prev, [name]: numVal }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nroRubro) {
        enqueueSnackbar('El número de rubro es requerido', { variant: 'warning' });
        return;
      }

      if (nroRubroParam && nroRubroParam !== 'nuevo') {
        const nro = parseInt(nroRubroParam, 10);
        await noImputableService.updateNoImputable(nro, {
          descripcioncta: formData.descripcioncta,
          nombreCuenta: formData.nombreCuenta,
          segmento: formData.segmento,
          totalizaEn: formData.totalizaEn === 0 ? undefined : formData.totalizaEn,
          saldo: formData.saldo
        });
      } else {
        await noImputableService.createNoImputable(formData);
      }

      enqueueSnackbar('No imputable guardado exitosamente', { variant: 'success' });
      goBackOrListado();
    } catch (error: any) {
      console.error('Error al guardar no imputable:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar el no imputable', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {nroRubroParam && nroRubroParam !== 'nuevo' ? 'Editar No Imputable' : 'Nuevo No Imputable'}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="nroRubro" 
                  label="Código (Nro. Rubro)" 
                  type="number"
                  value={formData.nroRubro || ''} 
                  onChange={handleChange} 
                  fullWidth 
                  required 
                  disabled={!!(nroRubroParam && nroRubroParam !== 'nuevo')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  name="nombreCuenta"
                  label="Nombre de cuenta"
                  value={formData.nombreCuenta || ''}
                  onChange={handleChange}
                  onBlur={handleNombreCuentaBlur}
                  fullWidth
                  inputProps={{ maxLength: MAX_NO_IMPUTABLE_NOMBRE_LEN }}
                  helperText={`${(formData.nombreCuenta || '').trim().length}/${MAX_NO_IMPUTABLE_NOMBRE_LEN}`}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="descripcioncta"
                  label="Descripción"
                  value={formData.descripcioncta || ''}
                  onChange={handleChange}
                  onBlur={handleDescripcionBlur}
                  fullWidth
                  multiline
                  rows={3}
                  inputProps={{ maxLength: MAX_NO_IMPUTABLE_DESCRIPCION_LEN }}
                  helperText={`${(formData.descripcioncta || '').trim().length}/${MAX_NO_IMPUTABLE_DESCRIPCION_LEN}`}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="segmento" 
                  label="Segmento (Nivel)" 
                  type="number"
                  value={formData.segmento ?? ''} 
                  onChange={handleChange} 
                  fullWidth
                  inputProps={{ min: 0 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={rubroOptions}
                  getOptionLabel={(opt) => `${opt.nroRubro} — ${opt.nombreCuenta || opt.descripcioncta || ''}`}
                  value={rubroOptions.find((r) => r.nroRubro === formData.totalizaEn) ?? null}
                  onChange={(_e, val) => setFormData((prev) => ({ ...prev, totalizaEn: val?.nroRubro ?? undefined }))}
                  isOptionEqualToValue={(opt, val) => opt.nroRubro === val.nroRubro}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Totaliza en"
                      placeholder="Buscar por código o nombre..."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  name="saldo" 
                  label="Saldo" 
                  type="number"
                  value={formData.saldo ?? ''} 
                  onChange={handleChange} 
                  fullWidth
                  inputProps={{ step: 0.01 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={goBackOrListado} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NoImputableForm;
